import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import TimePicker from './TimePicker';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  professional: { id: string; name: string };
  canManage: boolean;
};

const ProfessionalTimeOffModal: React.FC<Props> = ({ isOpen, onClose, businessId, professional, canManage }) => {
  const [timeOffs, setTimeOffs] = useState<any[]>([]);
  const [timeOffsLoading, setTimeOffsLoading] = useState(false);
  const [timeOffsError, setTimeOffsError] = useState<string | null>(null);

  const [isMobile, setIsMobile] = useState(false);

  const [actionsDropdownOpen, setActionsDropdownOpen] = useState<string | null>(null);
  const [actionsDropdownAnchor, setActionsDropdownAnchor] = useState<{ id: string; rect: DOMRect; anchorEl: HTMLElement } | null>(null);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [editingTimeOff, setEditingTimeOff] = useState<any>(null);
  const [timeOffSaving, setTimeOffSaving] = useState(false);

  const [timeOffFormData, setTimeOffFormData] = useState({
    label: '',
    startDate: '',
    endDate: '',
    isAllDay: false,
    startTime: '',
    endTime: ''
  });

  const [timeOffToDelete, setTimeOffToDelete] = useState<{ id: string; label: string } | null>(null);
  const [isDeletingTimeOff, setIsDeletingTimeOff] = useState(false);

  const loadTimeOffs = async () => {
    if (!businessId || !professional.id) return;
    try {
      setTimeOffsLoading(true);
      setTimeOffsError(null);
      const data = await apiService.ownerGetProfessionalTimeOffs(businessId, professional.id);
      setTimeOffs(data || []);
    } catch (e) {
      console.error('Error loading time offs:', e);
      setTimeOffsError('Failed to load time offs');
    } finally {
      setTimeOffsLoading(false);
    }
  };

  // Close dropdown when clicking outside, scrolling, or resizing
  useEffect(() => {
    if (!actionsDropdownOpen || !actionsDropdownAnchor) return;

    const handleGlobalPointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      // Click inside menu
      if (dropdownMenuRef.current && dropdownMenuRef.current.contains(target)) return;
      // Click on the anchor button
      if (actionsDropdownAnchor.anchorEl && actionsDropdownAnchor.anchorEl.contains(target as Node)) return;

      setActionsDropdownOpen(null);
      setActionsDropdownAnchor(null);
    };

    const handleScrollOrResize = () => {
      setActionsDropdownOpen(null);
      setActionsDropdownAnchor(null);
    };

    document.addEventListener('mousedown', handleGlobalPointer);
    document.addEventListener('touchstart', handleGlobalPointer, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    // capture scrolls from any ancestor (including the modal body)
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      document.removeEventListener('mousedown', handleGlobalPointer);
      document.removeEventListener('touchstart', handleGlobalPointer as any);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [actionsDropdownOpen, actionsDropdownAnchor]);

  useEffect(() => {
    if (!isOpen) return;
    loadTimeOffs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, businessId, professional.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 639px)'); // Tailwind `sm` starts at 640px

    const update = () => setIsMobile(mql.matches);
    update();

    // Safari < 14 fallback: addListener/removeListener
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }

    (mql as any).addListener(update);
    return () => (mql as any).removeListener(update);
  }, []);

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    try {
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
      if (typeof date === 'string' && date.includes('T')) {
        const datePart = date.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
      }
      const dateObj = typeof date === 'string' ? new Date(date + 'T00:00:00') : date;
      if (isNaN(dateObj.getTime())) return '';
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleOpenTimeOffModal = (timeOff?: any) => {
    if (!canManage) return;

    if (timeOff) {
      setEditingTimeOff(timeOff);
      setTimeOffFormData({
        label: timeOff.label || '',
        startDate: formatDateForInput(timeOff.startDate),
        endDate: formatDateForInput(timeOff.endDate),
        isAllDay: timeOff.isAllDay ?? true,
        startTime: timeOff.startTime ? convert24To12(timeOff.startTime) : '',
        endTime: timeOff.endTime ? convert24To12(timeOff.endTime) : ''
      });
    } else {
      setEditingTimeOff(null);
      setTimeOffFormData({
        label: '',
        startDate: '',
        endDate: '',
        isAllDay: false,
        startTime: '',
        endTime: ''
      });
    }

    setIsTimeOffModalOpen(true);
  };

  const handleCloseTimeOffModal = () => {
    setIsTimeOffModalOpen(false);
    setEditingTimeOff(null);
    setTimeOffFormData({
      label: '',
      startDate: '',
      endDate: '',
      isAllDay: false,
      startTime: '',
      endTime: ''
    });
  };

  const handleSaveTimeOff = async () => {
    if (!canManage) return;
    if (!businessId || !professional.id) return;

    // Basic validation (mirrors BusinessDashboard)
    if (!timeOffFormData.label.trim()) {
      toast.error('Please enter a label for the time off');
      return;
    }
    if (!timeOffFormData.startDate) {
      toast.error('Please select a start date');
      return;
    }
    if (!timeOffFormData.endDate) {
      toast.error('Please select an end date');
      return;
    }
    if (timeOffFormData.endDate < timeOffFormData.startDate) {
      toast.error('End date must be after start date');
      return;
    }
    if (!timeOffFormData.isAllDay) {
      if (!timeOffFormData.startTime) {
        toast.error('Please select a start time');
        return;
      }
      if (!timeOffFormData.endTime) {
        toast.error('Please select an end time');
        return;
      }
    }

    try {
      setTimeOffSaving(true);
      const payload = {
        label: timeOffFormData.label.trim(),
        startDate: timeOffFormData.startDate,
        endDate: timeOffFormData.endDate,
        isAllDay: !!timeOffFormData.isAllDay,
        ...(timeOffFormData.isAllDay
          ? {}
          : {
              startTime: convert12To24(timeOffFormData.startTime),
              endTime: convert12To24(timeOffFormData.endTime)
            })
      };

      if (editingTimeOff?.id) {
        await apiService.ownerUpdateProfessionalTimeOff(businessId, professional.id, editingTimeOff.id, payload);
        toast.success('Time off updated successfully');
      } else {
        await apiService.ownerCreateProfessionalTimeOff(businessId, professional.id, payload);
        toast.success('Time off added successfully');
      }

      handleCloseTimeOffModal();
      await loadTimeOffs();
    } catch (error: any) {
      console.error('Error saving time off:', error);
      toast.error(error.response?.data?.message || (editingTimeOff ? 'Failed to update time off' : 'Failed to add time off'));
    } finally {
      setTimeOffSaving(false);
    }
  };

  const handleDeleteTimeOff = async () => {
    if (!canManage) return;
    if (!timeOffToDelete) return;
    try {
      setIsDeletingTimeOff(true);
      await apiService.ownerDeleteProfessionalTimeOff(businessId, professional.id, timeOffToDelete.id);
      setTimeOffToDelete(null);
      toast.success('Time off deleted successfully');
      await loadTimeOffs();
    } catch (error: any) {
      console.error('Error deleting time off:', error);
      toast.error(error.response?.data?.message || 'Failed to delete time off');
    } finally {
      setIsDeletingTimeOff(false);
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper functions for time conversion (24-hour <-> 12-hour)
  const convert24To12 = (time24: string): string => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12.toString().padStart(2, '0')}:${minutes || '00'} ${ampm}`;
  };

  const convert12To24 = (time12: string): string => {
    if (!time12) return '';
    const parts = time12.trim().split(' ');
    if (parts.length < 2) return '';
    const [time, period] = parts;
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours, 10);
    const min = minutes || '00';
    
    if (period.toUpperCase() === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${min}`;
  };

  // Time offs are business-local DATE/TIME concepts. Do NOT use `new Date('YYYY-MM-DD')` (UTC-shift risk).
  const parseLocalDate = (ymd: string): Date => {
    const parts = ymd.split('-');
    if (parts.length !== 3) return new Date(ymd);
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  const toYmd = (v: any): string => {
    if (!v) return '';
    const s = String(v);
    return s.includes('T') ? s.split('T')[0] : s;
  };

  const canInteract = canManage;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-visible">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">Time Off</h2>
            <p className="text-sm text-gray-600 mt-1 truncate">{professional.name}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => handleOpenTimeOffModal()}
              className="hidden sm:inline-flex aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canInteract}
              type="button"
              title={!canInteract ? 'Owner-only' : 'Add time off'}
            >
              <i className="bi bi-plus"></i>
              Time Off
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500" type="button">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 overflow-auto max-h-[calc(90vh-140px)] space-y-4 md:space-y-6">
          {!canManage && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm font-medium">Only Business Owners can manage another personnel’s time off.</p>
            </div>
          )}

          {/* Mobile-only Add Time Off button */}
          <button
            onClick={() => handleOpenTimeOffModal()}
            className="sm:hidden aacore-btn2-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canInteract}
            type="button"
            title={!canInteract ? 'Owner-only' : 'Add time off'}
          >
            <i className="bi bi-plus"></i>
            Time Off
          </button>

          {timeOffsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading time offs...</p>
            </div>
          ) : timeOffsError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{timeOffsError}</p>
            </div>
          ) : timeOffs.length === 0 ? (
            <div className="text-center py-8">
              <i className="bi bi-calendar-x text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600 text-base">Create a time off entry to block the calendar</p>
              <p className="text-gray-600 text-base">and prevent bookings during that time.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {timeOffs.map((timeOff: any) => {
                const startYmd = toYmd(timeOff.startDate);
                const endYmd = toYmd(timeOff.endDate);
                const formatDate = (ymd: string) =>
                  parseLocalDate(ymd).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  });

                const isAllDay =
                  timeOff.isAllDay === true || timeOff.isAllDay === 'true' || timeOff.isAllDay === 1;

                return (
                  <div key={timeOff.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-2">{timeOff.label}</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex items-center gap-2">
                            <i className="bi bi-calendar3"></i>
                            <span>
                              {formatDate(startYmd)}
                              {startYmd && endYmd && startYmd !== endYmd && ` - ${formatDate(endYmd)}`}
                            </span>
                          </div>
                          {isAllDay ? (
                            <div className="flex items-center gap-2">
                              <i className="bi bi-clock"></i>
                              <span>All Day</span>
                            </div>
                          ) : timeOff.startTime && timeOff.endTime ? (
                            <div className="flex items-center gap-2">
                              <i className="bi bi-clock"></i>
                              <span>
                                {formatTime(timeOff.startTime)} - {formatTime(timeOff.endTime)}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="relative work-schedule-actions-dropdown-container">
                          <button
                            onClick={() => {
                              if (!canInteract) return;
                              const nextId = `timeoff-${timeOff.id}`;
                              if (actionsDropdownOpen === nextId) {
                                setActionsDropdownOpen(null);
                                setActionsDropdownAnchor(null);
                                return;
                              }

                              const el = document.getElementById(`timeoff-actions-btn-${timeOff.id}`);
                              const anchorEl = (el as HTMLElement | null) || (null as any);
                              const rect = (anchorEl?.getBoundingClientRect?.() as DOMRect | undefined) || new DOMRect();
                              setActionsDropdownOpen(nextId);
                              if (anchorEl) setActionsDropdownAnchor({ id: nextId, rect, anchorEl });
                            }}
                            className={`flex items-center justify-center p-2 transition-colors ${
                              canInteract ? 'text-gray-400 hover:text-gray-600' : 'text-gray-200 cursor-not-allowed'
                            }`}
                            type="button"
                            title={canInteract ? 'Actions' : 'Owner-only'}
                            id={`timeoff-actions-btn-${timeOff.id}`}
                          >
                            <i
                              className={`bi bi-chevron-down transition-transform duration-200 text-lg ${
                                actionsDropdownOpen === `timeoff-${timeOff.id}` ? 'rotate-180' : ''
                              }`}
                            ></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add/Edit Time Off Modal (mirrors My Work Schedule) */}
        {isTimeOffModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">{editingTimeOff ? 'Edit Time Off' : 'Add Time Off'}</h2>
                <button onClick={handleCloseTimeOffModal} className="text-gray-400 hover:text-gray-500" disabled={timeOffSaving} type="button">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label htmlFor="pto-label" className="block text-sm font-medium text-gray-700 mb-2">
                    Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="pto-label"
                    type="text"
                    value={timeOffFormData.label}
                    onChange={(e) => setTimeOffFormData({ ...timeOffFormData, label: e.target.value })}
                    placeholder="e.g., Vacation, Sick Leave, Personal Day"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pto-start" className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="pto-start"
                      type="date"
                      value={timeOffFormData.startDate}
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        const newEndDate =
                          !timeOffFormData.endDate || timeOffFormData.endDate < newStartDate ? newStartDate : timeOffFormData.endDate;
                        setTimeOffFormData({ ...timeOffFormData, startDate: newStartDate, endDate: newEndDate });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                    />
                  </div>
                  <div>
                    <label htmlFor="pto-end" className="block text-sm font-medium text-gray-700 mb-2">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="pto-end"
                      type="date"
                      value={timeOffFormData.endDate}
                      onChange={(e) => setTimeOffFormData({ ...timeOffFormData, endDate: e.target.value })}
                      min={timeOffFormData.startDate}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={timeOffFormData.isAllDay}
                      onChange={(e) =>
                        setTimeOffFormData({
                          ...timeOffFormData,
                          isAllDay: e.target.checked,
                          ...(e.target.checked ? { startTime: '', endTime: '' } : {})
                        })
                      }
                      className="w-4 h-4 text-[#0B4778] border-gray-300 rounded focus:ring-[#0B4778]"
                    />
                    <span className="text-sm font-medium text-gray-700">All Day</span>
                  </label>
                </div>

                {!timeOffFormData.isAllDay && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="pto-start-time" className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <TimePicker
                        value={timeOffFormData.startTime}
                        onChange={(time) => setTimeOffFormData({ ...timeOffFormData, startTime: time })}
                        placeholder="--:-- --"
                        className="w-full"
                        minuteInterval="timeoff"
                        dropdownDirection={isMobile ? 'up' : 'down'}
                      />
                    </div>
                    <div>
                      <label htmlFor="pto-end-time" className="block text-sm font-medium text-gray-700 mb-2">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <TimePicker
                        value={timeOffFormData.endTime}
                        onChange={(time) => setTimeOffFormData({ ...timeOffFormData, endTime: time })}
                        placeholder="--:-- --"
                        className="w-full"
                        minuteInterval="timeoff"
                        alignRight={true}
                        dropdownDirection={isMobile ? 'up' : 'down'}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button onClick={handleCloseTimeOffModal} className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200" disabled={timeOffSaving} type="button">
                  <i className="bi bi-x"></i>
                  Cancel
                </button>
                <button
                  onClick={handleSaveTimeOff}
                  disabled={timeOffSaving}
                  className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                >
                  {timeOffSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check"></i>
                      {editingTimeOff ? 'Update' : 'Save'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {timeOffToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Delete Time Off</h2>
                <button onClick={() => setTimeOffToDelete(null)} className="text-gray-400 hover:text-gray-500" disabled={isDeletingTimeOff} type="button">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to delete <span className="font-semibold">{timeOffToDelete.label}</span>? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button onClick={() => setTimeOffToDelete(null)} className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200" disabled={isDeletingTimeOff} type="button">
                  <i className="bi bi-x"></i>
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTimeOff}
                  disabled={isDeletingTimeOff}
                  className="aacore-btn2-danger disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                >
                  {isDeletingTimeOff ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Time off actions dropdown (portal so it won't be clipped by scroll containers/header) */}
      {canInteract && actionsDropdownOpen && actionsDropdownAnchor && typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            className="fixed min-w-[200px] bg-white border border-gray-300 rounded-lg shadow-lg py-2"
            style={{
              zIndex: 100000,
              // Position above the button, right-aligned to it
              left: actionsDropdownAnchor.rect.right,
              top: actionsDropdownAnchor.rect.top - 8,
              transform: 'translate(-100%, -100%)'
            }}
          >
            <button
              onClick={() => {
                const id = actionsDropdownOpen;
                setActionsDropdownOpen(null);
                setActionsDropdownAnchor(null);

                // Find the timeOff based on open id
                const timeOffId = id.replace('timeoff-', '');
                const timeOff = (timeOffs || []).find((t: any) => String(t.id) === String(timeOffId));
                if (timeOff) handleOpenTimeOffModal(timeOff);
              }}
              className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
              type="button"
            >
              <i className="bi bi-pencil text-[#0B4778]"></i>
              Edit Time Off
            </button>
            <button
              onClick={() => {
                const id = actionsDropdownOpen;
                setActionsDropdownOpen(null);
                setActionsDropdownAnchor(null);
                const timeOffId = id.replace('timeoff-', '');
                const timeOff = (timeOffs || []).find((t: any) => String(t.id) === String(timeOffId));
                if (timeOff) setTimeOffToDelete({ id: timeOff.id, label: timeOff.label });
              }}
              className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
              type="button"
            >
              <i className="bi bi-trash text-red-600"></i>
              Delete Time Off
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProfessionalTimeOffModal;

